<template>
  <modal @close-modal="close">
    <div class="initiate-reset-password">
      <h2 class="my-0 text-center">Change Your Password</h2>

      <div class="justify-content-center mt-4">
        <div class="col-12 form-group form-label-group-floating">
          <input
            id="current-password"
            v-model="currentPassword"
            type="password"
            name="Current password"
            class="form-control"
          />
          <label
            class="font-weight-normal"
            for="password"
            style="color: var(--grey-2500)"
          >
            Current password
          </label>
        </div>
        <div class="col-12 form-group form-label-group-floating">
          <input
            id="new-password"
            v-model="newPassword"
            type="password"
            name="New password"
            class="form-control"
            @focus="showPasswordRules = true"
            @blur="showPasswordRules = false"
          />
          <label
            class="font-weight-normal"
            for="password"
            style="color: var(--grey-2500)"
          >
            New password
          </label>
          <div v-if="showPasswordRules" class="password-rules">
            <div
              v-for="item in passwordConditions"
              :key="item.type"
              class="col-md-12 col-12 px-0"
              :class="{ 'validate-item': item.valid === true }"
            >
              <font-awesome-icon
                v-if="!validateErrors.includes(item.type)"
                :icon="faCircleCheck"
                color="#01a824"
                class="mr-2"
              />
              <font-awesome-icon
                v-if="validateErrors.includes(item.type)"
                :icon="faCircleXmark"
                color="red"
                class="mr-2"
              />
              <span>{{ item.text }}</span>
            </div>
          </div>
        </div>
        <div class="col-12 form-group form-label-group-floating">
          <input
            id="confirm-password"
            v-model="confirmPassword"
            type="password"
            name="Confirm password"
            class="form-control"
          />
          <label
            class="font-weight-normal"
            for="password"
            style="color: var(--grey-2500)"
          >
            Confirm password
          </label>
        </div>
        <button :disabled="disableSubmitButton" @click="initiateResetPassword">
          <b v-if="!loadingBTNChange">Change password</b>
          <div v-else class="row justify-content-center align-items-center">
            <Spinner style="margin: 0 !important" />
          </div>
        </button>
      </div>
    </div>
  </modal>
</template>

<script setup>
// core
import { ref, computed } from 'vue';
import { useAPI } from '@/composables/useAPI';
import { emitter } from '@/core/emitter';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons/faCircleXmark';
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons/faCircleCheck';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useUserStore } from '@/stores/user';

// components
import Modal from '@/components/global/Modal.vue';
import Spinner from '@/components/global/Spinner.vue';

// Reset password
const loadingBTNChange = ref(false);
const { request: changePasswordAuthed } = useAPI('/user/changePasswordAuthed');

const emit = defineEmits(['closeModal']);
const userStore = useUserStore();
const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const showPasswordRules = ref(false);
const passwordConditions = ref([
  { type: 'min', text: 'At least 8 characters' },
  { type: 'upper', text: 'An uppercase character' },
  { type: 'lower', text: 'A lowercase character' },
  { type: 'number', text: 'At least 1 number' },
]);

const disableSubmitButton = computed(
  () =>
    loadingBTNChange.value ||
    currentPassword.value === '' ||
    newPassword.value === '' ||
    validateErrors.value.length > 0 ||
    confirmPassword.value !== newPassword.value,
);

const validateErrors = computed(() => {
  const errors = [];
  if (newPassword.value.length < 8) errors.push('min');
  if (!/[A-Z]/.test(newPassword.value)) errors.push('upper');
  if (!/[a-z]/.test(newPassword.value)) errors.push('lower');
  if (!/\d/.test(newPassword.value)) errors.push('number');

  return errors;
});

function close() {
  emit('closeModal');
}

async function initiateResetPassword() {
  loadingBTNChange.value = true;
  try {
    await changePasswordAuthed({
      oldPassword: currentPassword.value,
      newPassword: newPassword.value,
    });
    emitter.emit('showAlert', {
      shown: true,
      type: 'email-success',
      status: 'ok',
      title: 'Success!',
      detail: 'Your password has been updated successfully',
    });
    emit('closeModal');
  } catch (error) {
    if (error.message === 'Old password is incorrect') {
      emitter.emit('showAlert', {
        shown: true,
        type: 'email-fail',
        status: 'danger',
        title: 'Error! Current password is incorrect',
        detail: 'You must enter the password associated with this account.',
      });
    }
  } finally {
    userStore.setPasswordHighlighted(false);
    loadingBTNChange.value = false;
  }
}
</script>

<style scoped lang="scss">
@import '@/scss/variables.scss';
@import '@/scss/theme/variables.scss';

.form-details {
  color: $grey-2400;
  font-size: $font-size-sm;

  &.error {
    color: $red-400;
  }
  &.success {
    color: $green-500;
  }
}
.initiate-reset-password {
  max-width: 450px;
  background: $grey-500;
  padding: 1rem 2rem;
  color: $grey-2400;

  &-header {
    border-bottom: 1px solid $color-primary;
  }
}
h2 {
  color: $color-primary_01;
}
h4 {
  margin: 0;
  font-size: $font-size-base;
  color: $blue-1100;
}
h5 {
  color: $grey-2600;
}
p {
  margin: 0;
}
input {
  width: 100%;
  font-size: 16px;
}
button {
  padding: 1rem;
  width: 100%;
}

.password-rules {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  top: -140px;
  visibility: visible;
  width: fit-content;
  background-color: dimgray;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 5px 8px;
  text-align: left;
  /* Position the tooltip */
  position: absolute;
  z-index: 2;
}
</style>
